import React, { useCallback, useMemo, useState } from "react";

import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { graphql } from "relay-runtime";

import { useLazyLoadQuery } from "~/lib/relayUtil";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";

import type { SelectActionItemGroupQuery } from "~/__generated__/SelectActionItemGroupQuery.graphql";

import styles from "./SelectActionItemGroup.module.css";

const selectActionItemsQuery = graphql`
	query SelectActionItemGroupQuery {
		actionItemGroups {
			edges {
				node {
					id
					name
					order
					isDefaultGroup
				}
			}
		}
	}
`;

interface SelectActionItemOptionProps {
	id: string;
	name: string;
	onSelect(newId: string): void;
}

function SelectActionItemOption({ id, name, onSelect }: SelectActionItemOptionProps) {
	const onClick = useCallback(() => onSelect(id), [id, onSelect]);

	return (
		<Menu.RadioItem value={id} onSelect={onClick}>
			{name}
		</Menu.RadioItem>
	);
}

interface SelectActionItemGroupProps {
	groupId?: string;
	onGroupIdChanged(newGroupId?: string): void;
}

function SelectActionItemGroupInner({ groupId, onGroupIdChanged }: SelectActionItemGroupProps) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const data = useLazyLoadQuery<SelectActionItemGroupQuery>(
		selectActionItemsQuery,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const toggleOpen = useCallback(() => setIsOpen(isOpen => !isOpen), []);
	const onClose = useCallback(() => setIsOpen(false), []);

	const onItemSelected = useCallback(
		(newGroupId: string) => {
			onGroupIdChanged(newGroupId);
			onClose();
		},
		[onGroupIdChanged, onClose],
	);

	const actionItemGroups = useMemo(() => data.actionItemGroups.edges, [data]);
	const selectedActionItemGroupName = useMemo(() => {
		let defaultGroupName = "";
		for (let i = 0; i < actionItemGroups.length; i++) {
			if (actionItemGroups[i] && actionItemGroups[i]?.node.id === groupId) {
				return actionItemGroups[i]?.node.name;
			} else if (actionItemGroups[i]?.node.isDefaultGroup) {
				defaultGroupName = actionItemGroups[i]?.node.name || "";
			}
		}
		return defaultGroupName;
	}, [groupId, actionItemGroups]);

	return (
		<Menu open={isOpen}>
			<Menu.Trigger>
				<button className={cx(styles.triggerButton, { [styles.active]: isOpen })} onClick={toggleOpen}>
					{selectedActionItemGroupName}
					<FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
				</button>
			</Menu.Trigger>
			<Menu.Content>
				<Menu.Label>Action item group</Menu.Label>
				<Menu.RadioGroup value={groupId}>
					{actionItemGroups.map(actionItemGroup =>
						actionItemGroup?.node ? (
							<SelectActionItemOption
								key={actionItemGroup.node.id}
								id={actionItemGroup.node.id}
								name={actionItemGroup.node.name}
								onSelect={onItemSelected}
							/>
						) : null,
					)}
				</Menu.RadioGroup>
			</Menu.Content>
		</Menu>
	);
}

export default function SelectActionItemGroup({ groupId, onGroupIdChanged }: SelectActionItemGroupProps) {
	return (
		<React.Suspense fallback={<LoadingSpinner dimmer={false} inline />}>
			<SelectActionItemGroupInner groupId={groupId} onGroupIdChanged={onGroupIdChanged} />
		</React.Suspense>
	);
}
